import { useState, useEffect, useRef, useMemo, memo, useCallback } from 'react';
import { ShimmerText, renderBlock } from '../MessageBlockRenderer';

const MAX_VISIBLE = 3;
const GAP = 6;
const ENTER_DELAY = 180;
const CLOSE_DURATION = 400;

function getBlockKey(block, index) {
    if (block.tool_call_id) return block.tool_call_id;
    if (block.id) return block.id;
    return `${block.type}-${block.name || ''}-${index}`;
}

class LayoutEngine {
    constructor(track) {
        this.track = track;
        this.rid = null;
        this.observer = null;
    }

    sync() {
        if (!this.track) return;
        const all = Array.from(this.track.querySelectorAll('[data-si]'));
        if (!all.length) { this.track.style.height = '0px'; return; }

        const live = all.filter(el => el.dataset.exit !== '1');
        const exiting = all.filter(el => el.dataset.exit === '1');
        const visible = live.slice(-MAX_VISIBLE);
        const hidden = live.slice(0, -MAX_VISIBLE);

        let y = 0;
        visible.forEach(el => {
            el.style.top = `${y}px`;
            el.style.opacity = '1';
            y += el.offsetHeight + GAP;
        });

        hidden.forEach(el => {
            el.style.top = `${-(el.offsetHeight + GAP)}px`;
            el.style.opacity = '0';
        });

        exiting.forEach(el => { el.style.opacity = '0'; });

        this.track.style.height = `${y > 0 ? y - GAP : 0}px`;
    }

    getEnterTop() {
        if (!this.track) return 0;
        const live = Array.from(this.track.querySelectorAll('[data-si]:not([data-exit="1"]):not([data-entering="1"])'));
        let h = 0;
        live.slice(-MAX_VISIBLE).forEach(el => { h += el.offsetHeight + GAP; });
        return h;
    }

    observe() {
        if (!this.track || this.observer) return;
        this.observer = new ResizeObserver(() => this.sync());
        this.observer.observe(this.track);
        this.track.querySelectorAll('[data-body]').forEach(el => this.observer.observe(el));
    }

    reobserve() {
        this.observer?.disconnect();
        this.observer = null;
        this.observe();
    }

    syncLoop(duration = 400) {
        if (this.rid) cancelAnimationFrame(this.rid);
        const end = performance.now() + duration;
        const loop = () => {
            this.sync();
            if (performance.now() < end) this.rid = requestAnimationFrame(loop);
            else this.rid = null;
        };
        this.rid = requestAnimationFrame(loop);
    }

    destroy() {
        if (this.rid) cancelAnimationFrame(this.rid);
        this.observer?.disconnect();
    }
}

const ChatStreamGroup = memo(function ChatStreamGroup({ blocks, isOpen, summary, renderOptions = {} }) {
    const [isExpanded, setIsExpanded] = useState(isOpen);
    const [showAll, setShowAll] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [streamItems, setStreamItems] = useState([]);

    const prevOpenRef = useRef(isOpen);
    const queueRef = useRef([]);
    const processingRef = useRef(false);
    const knownKeysRef = useRef(new Set());
    const trackRef = useRef(null);
    const layoutRef = useRef(null);

    const isDone = !isOpen;

    useEffect(() => () => layoutRef.current?.destroy(), []);

    const setTrackNode = useCallback((node) => {
        trackRef.current = node;
        if (node) {
            layoutRef.current?.destroy();
            layoutRef.current = new LayoutEngine(node);
            requestAnimationFrame(() => {
                layoutRef.current?.sync();
                layoutRef.current?.observe();
            });
        } else {
            layoutRef.current?.destroy();
            layoutRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (prevOpenRef.current && !isOpen) {
            queueRef.current = [];
            processingRef.current = false;
            setIsClosing(true);
            const timer = setTimeout(() => {
                setIsClosing(false);
                setIsExpanded(false);
                setStreamItems([]);
                knownKeysRef.current.clear();
            }, CLOSE_DURATION);
            return () => clearTimeout(timer);
        }
        prevOpenRef.current = isOpen;
    }, [isOpen]);

    const liveSummary = useMemo(() => {
        if (isDone && summary) return summary;
        if (!blocks || blocks.length === 0) return summary || 'Đang xử lý...';
        for (let i = blocks.length - 1; i >= 0; i--) {
            const b = blocks[i];
            if (b.type === 'thinking') continue;
            if (b.tool_content_message) return b.tool_content_message;
            if (b.name) return b.name;
        }
        return summary || 'Đang xử lý...';
    }, [blocks, summary, isDone]);

    const visibleBlocks = useMemo(() =>
        (blocks || []).filter(b => !b._merged),
    [blocks]);

    const blockMap = useMemo(() => {
        const map = new Map();
        visibleBlocks.forEach((block, i) => map.set(getBlockKey(block, i), block));
        return map;
    }, [visibleBlocks]);

    const processQueue = useCallback(async () => {
        if (processingRef.current) return;
        processingRef.current = true;

        while (queueRef.current.length > 0) {
            const key = queueRef.current.shift();

            setStreamItems(prev => [...prev, { key, entering: true }]);

            await new Promise(resolve => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setStreamItems(prev =>
                            prev.map(item =>
                                item.key === key ? { ...item, entering: false } : item
                            )
                        );
                        layoutRef.current?.sync();
                        layoutRef.current?.reobserve();
                        resolve();
                    });
                });
            });

            if (queueRef.current.length > 0) {
                await new Promise(r => setTimeout(r, ENTER_DELAY));
            }
        }

        processingRef.current = false;
    }, []);

    useEffect(() => {
        if (isDone || !isExpanded) return;

        let hasNew = false;
        visibleBlocks.forEach((block, i) => {
            const key = getBlockKey(block, i);
            if (!knownKeysRef.current.has(key)) {
                knownKeysRef.current.add(key);
                queueRef.current.push(key);
                hasNew = true;
            }
        });

        if (hasNew) processQueue();
    }, [visibleBlocks, isDone, isExpanded, processQueue]);

    useEffect(() => {
        if (!layoutRef.current) return;
        const id = requestAnimationFrame(() => layoutRef.current?.sync());
        return () => cancelAnimationFrame(id);
    }, [streamItems, isClosing]);

    useEffect(() => {
        if (!layoutRef.current || isDone) return;
        const id = requestAnimationFrame(() => layoutRef.current?.sync());
        return () => cancelAnimationFrame(id);
    }, [blocks, isDone]);

    if (visibleBlocks.length === 0) return null;

    const displayBlocks = (isDone || showAll)
        ? visibleBlocks
        : (visibleBlocks.length > MAX_VISIBLE ? visibleBlocks.slice(-MAX_VISIBLE) : visibleBlocks);

    const useAnimation = isExpanded && !showAll && (streamItems.length > 0 || isClosing);
    const enterTop = layoutRef.current?.getEnterTop() ?? 0;

    return (
        <div className="py-1">
            <div className="group/header flex items-center gap-1.5 mb-1">
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="group flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-700 transition-colors text-left"
                >
                    <ShimmerText isStreaming={!isDone}>
                        <span className="text-gray-500">{liveSummary}</span>
                    </ShimmerText>
                    <svg
                        className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
                {!isDone && isExpanded && visibleBlocks.length > MAX_VISIBLE && (
                    <button
                        onClick={() => setShowAll(!showAll)}
                        className="flex-shrink-0 p-0.5 text-gray-400 hover:text-gray-600 transition-colors opacity-0 group-hover/header:opacity-100"
                        title={showAll ? 'Thu gọn' : 'Xem tất cả'}
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {showAll ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            ) : (
                                <>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5 5 5" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l5-5 5 5" />
                                </>
                            )}
                        </svg>
                    </button>
                )}
            </div>

            {isExpanded && (
                useAnimation ? (
                    <div
                        ref={setTrackNode}
                        className="relative overflow-hidden"
                        style={{ transition: 'height .45s cubic-bezier(.22,1,.36,1)' }}
                    >
                        {streamItems.map(item => {
                            const block = blockMap.get(item.key);
                            if (!block) return null;

                            return (
                                <div
                                    key={item.key}
                                    data-si={item.key}
                                    data-exit={isClosing ? '1' : '0'}
                                    data-entering={item.entering ? '1' : '0'}
                                    className="absolute left-0 right-0"
                                    style={{
                                        top: item.entering ? enterTop : undefined,
                                        opacity: item.entering || isClosing ? 0 : undefined,
                                        transition: 'top .45s cubic-bezier(.22,1,.36,1), opacity .35s ease',
                                        willChange: 'top, opacity',
                                        contain: 'layout style',
                                    }}
                                >
                                    <div data-body="">
                                        {renderBlock(block, 0, renderOptions)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="space-y-0">
                        {displayBlocks.map((block, index) => renderBlock(block, index, {
                            isFromHistory: isDone,
                            ...renderOptions,
                        }))}
                        {isDone && (
                            <div className="flex items-center gap-1.5 text-[13px] text-gray-500 py-1.5">
                                <span
                                    className="inline-block rounded-full shrink-0 bg-green-500"
                                    style={{ width: 7, height: 7 }}
                                />
                                <span>Hoàn thành</span>
                            </div>
                        )}
                    </div>
                )
            )}
        </div>
    );
});

export default ChatStreamGroup;
